import React, {memo, useEffect, useMemo, useState} from 'react'
import {Handle, Position, NodeProps, useNodes, type Node, useReactFlow, type Edge} from 'reactflow'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'
import {DataNode, DelayTime, DelayUnit, IDelayUnit, MetaDataWithDelay} from '../../FlowTypes'
import {NodeType, TIMING} from '../../../../../../Utils/Constant/FlowConstants'
import {useFlowNodeDelete} from '../hooks/useFlowNodeDelete'

export function validateTimeDelayNode(data: DataNode<MetaDataWithDelay>) {
  const delay = data?.metaData?.delay
  if (!delay) return 'Time delay is required'
  if (delay.type === DelayTime.RELATIVE) {
    if (
      typeof delay.value !== 'number' ||
      isNaN(delay.value) ||
      delay.value <= 0 ||
      !Number.isInteger(delay.value)
    ) {
      return 'Please enter a valid number'
    }
  }

  return null
}

export interface TimeDelayNodeProps extends NodeProps {
  pushToUndoStack?: (nodes: Node[], edges: Edge[]) => void
  isUndoRedoOperation?: React.MutableRefObject<boolean>
}

const TimeDelayNode: React.FC<TimeDelayNodeProps> = ({
  id,
  selected,
  pushToUndoStack,
  isUndoRedoOperation,
}) => {
  const {setNodes, setEdges, getEdges} = useReactFlow()
  const nodes = useNodes()
  const edges = getEdges()
  const {t} = useI18n()

  const isSelected = selected
  const node = useMemo(() => nodes.find((n) => n.id === id), [nodes, id]) as Node<
    DataNode<MetaDataWithDelay>,
    NodeType
  >

  const [displayData, setDisplayData] = useState<IDelayUnit>({
    type: DelayTime.RELATIVE,
    value: 1,
    unit: DelayUnit.MINUTES,
  })

  const [hovered, setHovered] = useState(false)

  useEffect(() => {
    if (node?.data?.metaData?.delay) {
      const delay = node.data.metaData.delay
      setDisplayData({
        type: delay.type || DelayTime.RELATIVE,
        value:
          delay.type === DelayTime.RELATIVE
            ? typeof delay.value === 'number'
              ? delay.value
              : 1
            : typeof delay.value === 'string'
            ? delay.value
            : '',
        unit: delay.unit || DelayUnit.MINUTES,
      })
    }
  }, [node?.data?.metaData?.delay])

  const item = TIMING[0]

  const renderLabel = useMemo(() => {
    if (!displayData.type) {
      return 'Waiting...'
    }
    if (displayData.type === DelayTime.RELATIVE) {
      return 'Waiting for: '
    }
    if (displayData.type === DelayTime.ABSOLUTE) {
      return 'Waiting until: '
    }
    return 'Waiting...'
  }, [displayData.type])

  const renderValue = useMemo(() => {
    if (!displayData.value) {
      return 'No value'
    }

    if (displayData.type === DelayTime.RELATIVE) {
      return `${displayData.value} ${displayData.unit}`
    }

    if (displayData.type === DelayTime.ABSOLUTE && typeof displayData.value === 'string') {
      const [date, time] = displayData.value.split('T')
      if (date && time) {
        const [, month, day] = date.split('-')
        let [hour, minute] = time.split(':')
        let h = parseInt(hour, 10)
        const ampm = h >= 12 ? 'PM' : 'AM'
        h = h % 12
        if (h === 0) h = 12
        return `${month}-${day} ${h}:${minute} ${ampm}`
      }
      return displayData.value
    }

    return displayData.value
  }, [displayData])

  const {handleDelete} = useFlowNodeDelete({
    nodes,
    edges,
    setNodes,
    setEdges,
    pushToUndoStack,
    isUndoRedoOperation,
  })

  return (
    <div
      className={`p-3 rounded bg-white text-center position-relative shadow-sm${
        isSelected ? ' border border-2 border-primary' : ''
      } nopan${hovered ? ' node-hover' : ''}`}
      style={{width: 250, minHeight: 90}}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={t('FLOW.NODE.DELAY_DESC')}
    >
      {node?.data && (node.data as any)?.deletable !== false && (
        <button
          className='btn btn-link p-0 position-absolute end-0 top-0 m-2'
          style={{zIndex: 10}}
          title='Delete Node'
          onClick={(e) => handleDelete(id, node, e)}
        >
          <i className='bi bi-trash text-danger fs-5'></i>
        </button>
      )}
      <Handle
        type='target'
        position={Position.Top}
        id='timedelay-top'
        style={{background: 'var(--bs-gray-600)'}}
      />
      <div className='d-flex flex-column gap-2'>
        <div className='d-flex align-items-center gap-2'>
          <div
            className='automation-icon-container'
            style={{
              backgroundColor: `${item.bgColor}`,
            }}
          >
            <img src={toAbsoluteUrl(item.svg || '')} alt={item.icon} className='automation-icon' />
          </div>

          <span
            className='text-truncate fw-medium'
            style={{maxWidth: '140px'}}
            title={t('FLOW.NODE.DELAY_DESC')}
          >
            {t(`FLOW.NODE.${item.type.toUpperCase()}`)}
          </span>
        </div>

        <div
          className='d-grid'
          style={{
            gridTemplateColumns: 'auto 1fr',
            alignItems: 'start',
            width: '100%',
            gap: '0.5rem',
          }}
        >
          <p
            className='mb-0 small text-truncate'
            style={{
              maxWidth: 120,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={renderLabel}
          >
            {renderLabel}
          </p>
          <p
            className='mb-0 small text-truncate'
            style={{
              maxWidth: 120,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 600,
              display: 'flex',
            }}
          >
            {renderValue}
          </p>
        </div>
      </div>
      <Handle
        type='source'
        position={Position.Bottom}
        id='timedelay-bottom'
        className='bg-warning'
      />
    </div>
  )
}

export default memo(TimeDelayNode)
